<template>
  <div>
    <v-form @submit.prevent="submitResponse">
      <p class="mb-3">Using the data in the table below, answer each of the questions below:</p>
      <p class="mb-5 pl-14">
        <chemical-latex content="A + 2B -> C + D" />
      </p>

      <v-simple-table style="max-width: 400px" class="mb-5">
        <thead>
          <tr>
            <th>
              <stemble-latex content="$\text{Trial}$" />
            </th>
            <th style="text-align: center">
              <stemble-latex content="$\text{[A] (M)}$" />
            </th>
            <th style="text-align: center">
              <stemble-latex content="$\text{[B] (M)}$" />
            </th>
            <th style="text-align: center">
              <stemble-latex content="$\text{Rate (M/s)}$" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="text-align: center">
              <stemble-latex content="$1$" />
            </td>
            <td style="text-align: center">
              <number-value :value="Aconc1" />
            </td>
            <td style="text-align: center">
              <number-value :value="Bconc1" />
            </td>
            <td style="text-align: center">
              <latex-number :number="rate1.toFixed(5)" />
            </td>
          </tr>
          <tr>
            <td style="text-align: center">
              <stemble-latex content="$2$" />
            </td>
            <td style="text-align: center">
              <number-value :value="Aconc1" />
            </td>
            <td style="text-align: center">
              <latex-number :number="Bconc2.toFixed(3)" />
            </td>
            <td style="text-align: center">
              <latex-number :number="rate2.toFixed(5)" />
            </td>
          </tr>
          <tr>
            <td style="text-align: center">
              <stemble-latex content="$3$" />
            </td>
            <td style="text-align: center">
              <latex-number :number="Aconc3.toFixed(3)" />
            </td>
            <td style="text-align: center">
              <number-value :value="Bconc1" />
            </td>
            <td style="text-align: center">
              <latex-number :number="rate3.toFixed(5)" />
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <p class="mb-n3">a) What is the order with respect to reactant A?</p>
      <v-radio-group v-model="inputs.orderA" class="pl-8 mb-0" :disabled="!allowEditing">
        <v-radio
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :label="option.condition"
        />
      </v-radio-group>

      <p class="mb-n3 mt-1">b) What is the order with respect to reactant B?</p>

      <v-radio-group v-model="inputs.orderB" class="pl-8 mb-0" :disabled="!allowEditing">
        <v-radio
          v-for="option in options"
          :key="option.value"
          :value="option.value"
          :label="option.condition"
        />
      </v-radio-group>

      <p class="mb-2 mt-1">
        c) What is the rate constant for the reaction? <b>Note.</b> Please enter the numerical
        answer only (i.e. no units).
      </p>

      <calculation-input
        v-model="inputs.rateConstantAnswer"
        prepend-text="$\text{k:}$"
        clas="mb-3"
        :disabled="!allowEditing"
      />
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import ChemicalLatex from '../displayers/ChemicalLatex';
import LatexNumber from '../displayers/LatexNumber';
import CalculationInput from '../inputs/CalculationInput';
import NumberValue from '../NumberValue';
import StembleLatex from '@/tasks/components/StembleLatex';

export default {
  name: 'Question89',
  components: {
    StembleLatex,
    ChemicalLatex,
    LatexNumber,
    CalculationInput,
    NumberValue,
  },
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      // Put a list of inputs or computed properties here to be sent for marking
      inputs: {
        orderA: null,
        orderB: null,
        rateConstantAnswer: null,
      },
      options: [
        {condition: 'Zeroth Order', value: '0'},
        {condition: 'First Order', value: '1'},
        {condition: 'Second Order', value: '2'},
        {condition: 'Third Order', value: '3'},
      ],
    };
  },
  computed: {
    Aconc1() {
      return this.taskState.getValueBySymbol('Aconc1').content;
    },
    Bconc1() {
      return this.taskState.getValueBySymbol('Bconc1').content;
    },
    scaleA() {
      return this.taskState.getValueBySymbol('scaleA').content.value;
    },
    scaleB() {
      return this.taskState.getValueBySymbol('scaleB').content.value;
    },
    orderA() {
      return this.taskState.getValueBySymbol('orderA').content.value;
    },
    orderB() {
      return this.taskState.getValueBySymbol('orderB').content.value;
    },
    rateConstant() {
      return this.taskState.getValueBySymbol('rateConstant').content;
    },
    rate1() {
      return (
        this.rateConstant.toFloat() *
        this.Aconc1.toFloat() ** this.orderA *
        this.Bconc1.toFloat() ** this.orderB
      );
    },
    Bconc2() {
      return this.Bconc1.toFloat() * this.scaleB;
    },
    rate2() {
      return (
        this.rateConstant.toFloat() *
        this.Aconc1.toFloat() ** this.orderA *
        this.Bconc2 ** this.orderB
      );
    },
    Aconc3() {
      return this.Aconc1.toFloat() * this.scaleA;
    },
    rate3() {
      return (
        this.rateConstant.toFloat() *
        this.Aconc3 ** this.orderA *
        this.Bconc1.toFloat() ** this.orderB
      );
    },
  },
};
</script>
